// session.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { requestToken } from "../../utils/auth.utils";
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ApiCacheService } from './api-cache.service';
import { EncryptionService } from './encryption.service';
@Injectable({ providedIn: 'root' })
export class SessionService {
    private sessionId: string | null = null;
    userCurrentTime: any;
    userExpirationTime: any;
    refToken: any;
    encryptedData: any;
    decryptedData: any;
    constructor(private http: HttpClient, private apiCache: ApiCacheService,
        private afAuth: AngularFireAuth, private encryptionService: EncryptionService) {
        // restore sessionId if user refreshes
        this.sessionId = localStorage.getItem('sessionId');
    }

    async startSession() {
        this.encryptedData = localStorage.getItem('user');
        this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
        const user = JSON.parse(this.decryptedData || "{}");
        const token: any = user?.stsTokenManager?.accessToken;

        const expirationTime = user?.stsTokenManager?.expirationTime;
        const currentTime = new Date().getTime() / 1000;
        this.userCurrentTime = new Date(currentTime * 1000);
        this.userExpirationTime = new Date(expirationTime);

        let authToken = token;

        // Refresh if expired, OR if there was never a cached token at all (no
        // user signed in yet). The latter matters because comparing a valid
        // Date to `new Date(undefined)` (Invalid Date, from a missing
        // expirationTime) is always false in JS — so relying on
        // userCurrentTime >= userExpirationTime alone silently skips this
        // branch and falls through to building HttpHeaders with an undefined
        // Authorization value, which throws inside Angular's HttpHeaders.
        if (!authToken || this.userCurrentTime >= this.userExpirationTime) {
            const Reftoken = await requestToken(this.afAuth);
            if (Reftoken) {
                this.refToken = Reftoken;
                authToken = this.refToken;
            } else {
                throw new Error("Unable to refresh token");
            }
        }

        const headers = new HttpHeaders({
            "Content-Type": "application/json",
            "Authorization": authToken
        });

        if (this.sessionId) {
            // session already active, don’t start again
            return;
        }
     

        const body = new HttpParams()
            .set("action", "session_start")
           // .set("start_time", start_time)
            .toString();

        return this.http.post(
            "https://248tu96qta.execute-api.us-west-1.amazonaws.com/default/pwa-save-session-data",
            body,
            { headers }
        ).subscribe({
            next: (res: any) => {
                this.sessionId = res.session_id;   // backend returns session_id
                localStorage.setItem('sessionId', this.sessionId!);
            },
            // A dropped connection (status 0) is not worth an unhandled error; the
            // next page load starts a session again.
            error: () => { },
        });

    }

    async endSession() {
        this.encryptedData = localStorage.getItem('user');
        
        // Check if user data exists before decrypting
        if (!this.encryptedData || this.encryptedData === 'null' || this.encryptedData === 'undefined') {
            // console.warn('No user data found in localStorage for endSession'); //__CONSOLE_LOG__
            return;
        }

        this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
        
        // Check if decryption was successful
        if (!this.decryptedData) {
            // console.warn('Failed to decrypt user data in endSession'); //__CONSOLE_LOG__
            return;
        }

        const user = JSON.parse(this.decryptedData || "{}");
        const token: any = user?.stsTokenManager?.accessToken;

        if (!token) {
            // console.warn('No access token found in user data'); //__CONSOLE_LOG__
            return;
        }

        const expirationTime = user?.stsTokenManager?.expirationTime;
        const currentTime = new Date().getTime() / 1000;
        this.userCurrentTime = new Date(currentTime * 1000);
        this.userExpirationTime = new Date(expirationTime);

        let authToken = token;

        // Refresh token if expired
        if (this.userCurrentTime >= this.userExpirationTime) {
            const Reftoken = await requestToken(this.afAuth);
            if (Reftoken) {
                this.refToken = Reftoken;
                authToken = this.refToken;
            } else {
                throw new Error("Unable to refresh token");
            }
        }

        const headers = new HttpHeaders({
            "Content-Type": "application/json",
            "Authorization": authToken
        });

        if (!this.sessionId) return;


        const body = new HttpParams()
            .set("action", "session_end")
          //  .set("end_time", end_time)
            .set("session_id", this.sessionId)
            .toString();

        return this.http.post(
            "https://248tu96qta.execute-api.us-west-1.amazonaws.com/default/pwa-save-session-data",
            body,
            { headers }
        ).subscribe({
            next: () => {
                localStorage.removeItem('sessionId');
                this.sessionId = null;
            },
            // Sign-out and tab close often lose the connection mid-request; the
            // session end is best-effort.
            error: () => { },
        });

    }
}
